import React, { useState, useEffect } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { userRequest } from "../../api/requestMethods";
import { CURRENCY } from "../../config/market";

// Flat, solid-colour faceted rail (no gradients, no emoji). Facets and counts
// come straight from the unified /posts/catalog response.

const VENDOR_TYPES = [
  { key: "inhouse", label: "Lusuku", color: "#03660C" },
  { key: "retailer", label: "Retailer", color: "#2f80ed" },
  { key: "wholesaler", label: "Wholesaler", color: "#8e44ad" },
  { key: "brand", label: "Brand store", color: "#e08a1e" },
];
const VT_DOT = VENDOR_TYPES.reduce((m, vt) => ({ ...m, [vt.key]: vt.color }), {});

const Rail = styled.aside`
  background: #fff;
  border: 1px solid #eef1f2;
  border-radius: 12px;
  width: 200px;
  flex-shrink: 0;
  align-self: flex-start;
  position: sticky;
  top: 12px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(20, 40, 45, 0.06);
  @media (max-width: 900px) {
    width: 100%;
    position: static;
  }
`;
const Group = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid #eef1f2;
  &:last-child { border-bottom: 0; }
`;
const H = styled.h4`
  margin: 0 0 10px;
  font-size: 11.5px;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: #6b7a80;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const Reset = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #03660c;
  cursor: pointer;
  text-transform: none;
  letter-spacing: 0;
`;
const Opt = styled.label`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  min-width: 0;
  gap: 8px;
  padding: 5px 0;
  font-size: 13px;
  color: ${(p) => (p.$active ? "#03660c" : "#33454b")};
  font-weight: ${(p) => (p.$active ? 700 : 400)};
  cursor: pointer;
`;
const OptLabel = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Count = styled.span`
  margin-left: auto;
  flex-shrink: 0;
  color: #6b7a80;
  font-size: 11px;
  background: #f4f6f7;
  padding: 1px 7px;
  border-radius: 999px;
`;
const Dot = styled.span`
  width: 8px; height: 8px; border-radius: 50%;
  background: ${(p) => p.color}; display: inline-block; flex-shrink: 0;
`;
const PriceRow = styled.div`
  display: flex; align-items: center; gap: 7px;
`;
const NumInput = styled.input`
  width: 100%;
  border: 1px solid #e4e8ea;
  border-radius: 8px;
  padding: 7px 8px;
  font-size: 10px;
  color: #1f2a2e;
  outline: none;
  &:focus { border-color: #03660c; }
  /* Muted, lighter placeholder so a hint (e.g. the available range) is clearly
     distinguishable from a value the user actually typed. */
  &::placeholder {
    color: #aab4b8;
    opacity: 1;
    font-weight: 400;
    font-style: italic;
  }
`;
const ApplyBtn = styled.button`
  margin-top: 8px;
  width: 100%;
  border: 1px solid #03660c;
  background: #03660c;
  color: #fff;
  border-radius: 8px;
  padding: 7px;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
`;
const Toggle = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  font-size: 13.5px; color: #33454b;
`;
const Switch = styled.div`
  width: 38px; height: 22px; border-radius: 999px;
  background: ${(p) => (p.$on ? "#03660c" : "#cfd6d8")};
  position: relative; cursor: pointer; transition: background 0.15s ease;
  &::after {
    content: ""; position: absolute; top: 2px; left: ${(p) => (p.$on ? "18px" : "2px")};
    width: 18px; height: 18px; border-radius: 50%; background: #fff; transition: left 0.15s ease;
  }
`;
const Check = styled.input`accent-color: #03660c; width: 15px; height: 15px;`;
// Custom radio dot (a styled span, not a native <input>) so it can't be
// hijacked by a global input[type=radio] rule that was centring the native
// control and collapsing the row.
const RadioDot = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
  box-sizing: border-box;
  border: 2px solid ${(p) => (p.$active ? "#03660c" : "#bcc6ca")};
  position: relative;
  &::after {
    content: "";
    position: absolute;
    top: 3px;
    left: 3px;
    right: 3px;
    bottom: 3px;
    border-radius: 50%;
    background: ${(p) => (p.$active ? "#03660c" : "transparent")};
  }
`;

// Tick beside the ACTIVE category row — bold green alone is easy to miss when
// scanning a long list; the tick marks the row as the applied filter.
const Tick = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    style={{ flexShrink: 0 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const FilterRail = ({ categories = [], facets, value, onChange }) => {
  const v = value || {};
  const [pmin, setPmin] = useState(v.priceMin || "");
  const [pmax, setPmax] = useState(v.priceMax || "");
  useEffect(() => { setPmin(v.priceMin || ""); setPmax(v.priceMax || ""); }, [v.priceMin, v.priceMax]);

  // Vendor search (scales past the top-N popular vendors shown by default).
  const [vendorQuery, setVendorQuery] = useState("");
  const [vendorResults, setVendorResults] = useState([]);
  useEffect(() => {
    const q = vendorQuery.trim();
    if (!q) { setVendorResults([]); return; }
    let active = true;
    const t = setTimeout(() => {
      userRequest(`/posts/catalog/vendors?q=${encodeURIComponent(q)}&limit=15`)
        .then((r) => { if (active) setVendorResults(r?.data?.vendors || []); })
        .catch(() => { if (active) setVendorResults([]); });
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [vendorQuery]);

  // Count lookups from facets
  const catCount = {};
  (facets?.category || []).forEach((c) => { catCount[String(c.categoryId)] = c.count; });
  const vtCount = {};
  (facets?.vendorType || []).forEach((c) => { vtCount[c.vendorType] = c.count; });
  const topVendors = facets?.vendor || [];
  const vendorList = vendorQuery.trim() ? vendorResults : topVendors;

  const selectedVts = v.vendorTypes || [];
  const toggleVt = (key) => {
    const set = new Set(selectedVts);
    set.has(key) ? set.delete(key) : set.add(key);
    onChange({ vendorTypes: [...set] });
  };

  return (
    <Rail>
      <Group>
        <H>
          Categories
          {v.category ? <Reset onClick={() => onChange({ category: "" })}>reset</Reset> : null}
        </H>
        <Opt $active={!v.category} onClick={() => onChange({ category: "" })} as="div">
          {!v.category && <Tick />}
          <OptLabel>All products</OptLabel>
        </Opt>
        {categories
          .filter((c) => (c.category || "").toLowerCase() !== "all")
          .map((c) => (
          <Opt
            key={c._id}
            $active={v.category === c.category}
            onClick={() => onChange({ category: v.category === c.category ? "" : c.category })}
            as="div"
          >
            {v.category === c.category && <Tick />}
            <OptLabel>{c.category}</OptLabel>
            {catCount[String(c._id)] != null ? <Count>{catCount[String(c._id)]}</Count> : null}
          </Opt>
        ))}
      </Group>

      <Group>
        <H>Price Range ({CURRENCY})</H>
        <PriceRow>
          <NumInput
            type="number" min="0"
            placeholder={facets?.price?.min != null ? `Min ${Number(facets.price.min).toLocaleString("en-US")}` : "Min"}
            value={pmin} onChange={(e) => setPmin(e.target.value)}
          />
          <span style={{ color: "#9aa7ac" }}>–</span>
          <NumInput
            type="number" min="0"
            placeholder={facets?.price?.max != null ? `Max ${Number(facets.price.max).toLocaleString("en-US")}` : "Max"}
            value={pmax} onChange={(e) => setPmax(e.target.value)}
          />
        </PriceRow>
        <ApplyBtn onClick={() => onChange({ priceMin: pmin, priceMax: pmax })}>
          Apply price
        </ApplyBtn>
      </Group>

      <Group>
        <H>Vendor type</H>
        {VENDOR_TYPES.map((vt) => (
          <Opt
            key={vt.key}
            as="div"
            $active={selectedVts.includes(vt.key)}
            onClick={() => toggleVt(vt.key)}
          >
            <Check type="checkbox" readOnly checked={selectedVts.includes(vt.key)} />
            <Dot color={vt.color} />
            <OptLabel>{vt.label}</OptLabel>
            {vtCount[vt.key] != null ? <Count>{vtCount[vt.key]}</Count> : null}
          </Opt>
        ))}
      </Group>

      {(topVendors.length > 0 || vendorQuery.trim()) && (
        <Group>
          <H>
            Vendor
            {v.vendorId ? (
              <Reset onClick={() => onChange({ vendorId: "", vendorName: "" })}>reset</Reset>
            ) : null}
          </H>
          <div style={{ position: "relative", marginBottom: 8 }}>
            <NumInput
              type="text"
              placeholder="Search vendors…"
              value={vendorQuery}
              onChange={(e) => setVendorQuery(e.target.value)}
              style={{ paddingRight: 26 }}
            />
            {vendorQuery ? (
              <button
                type="button"
                onClick={() => setVendorQuery("")}
                aria-label="Clear vendor search"
                style={{
                  position: "absolute",
                  right: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  border: 0,
                  background: "transparent",
                  cursor: "pointer",
                  color: "#9aa7ac",
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: 1,
                  padding: 2,
                }}
              >
                ✕
              </button>
            ) : null}
          </div>
          {vendorList.map((vd) => (
            <Opt
              key={vd.vendorId}
              as="div"
              $active={v.vendorId === vd.vendorId}
              onClick={() =>
                onChange(
                  v.vendorId === vd.vendorId
                    ? { vendorId: "", vendorName: "" }
                    : { vendorId: vd.vendorId, vendorName: vd.name }
                )
              }
            >
              <Dot color={VT_DOT[vd.vendorType] || "#6b7a80"} />
              <OptLabel>{vd.name}</OptLabel>
              {vd.count != null ? <Count>{vd.count}</Count> : null}
            </Opt>
          ))}
          {vendorQuery.trim() && vendorList.length === 0 ? (
            <div style={{ fontSize: 12, color: "#9aa7ac", padding: "4px 2px" }}>
              No vendors found
            </div>
          ) : !vendorQuery.trim() ? (
            <div style={{ fontSize: 11, color: "#9aa7ac", padding: "4px 2px" }}>
              Top vendors — search to find more
            </div>
          ) : null}
        </Group>
      )}

      <Group>
        <H>Availability</H>
        <Toggle>
          <span>In stock only</span>
          <Switch $on={!!v.inStock} onClick={() => onChange({ inStock: !v.inStock })} />
        </Toggle>
      </Group>

      <Group>
        <H>Rating</H>
        {[
          { val: 0, label: "Any rating" },
          { val: 4, label: "4★ & up" },
          { val: 4.5, label: "4.5★ & up" },
        ].map((r) => {
          const on = Number(v.ratingMin || 0) === r.val;
          return (
            <Opt
              key={r.val}
              as="div"
              $active={on}
              onClick={() => onChange({ ratingMin: r.val })}
            >
              <RadioDot $active={on} />
              <OptLabel>{r.label}</OptLabel>
            </Opt>
          );
        })}
      </Group>
    </Rail>
  );
};

FilterRail.propTypes = {
  categories: PropTypes.array,
  facets: PropTypes.object,
  value: PropTypes.object,
  onChange: PropTypes.func,
};

export default FilterRail;
